import axios from "axios";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import imgData from "../assets/images/png/Logo@2x.png";

const normalizeCellValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === "" ||
    value === "None"
  ) {
    return "-";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object"
          ? JSON.stringify(item)
          : String(item),
      )
      .join(", ");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const getColumnValue = (row, column, rowIndex) => {
  if (column.serialNumber) {
    return rowIndex + 1;
  }

  const keys = Array.isArray(column.keys)
    ? column.keys
    : [column.key || column.dataKey].filter(Boolean);

  let selectedValue = "-";

  for (const key of keys) {
    const value = row?.[key];

    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      value !== "None"
    ) {
      selectedValue = value;
      break;
    }
  }

  if (typeof column.format === "function") {
    selectedValue = column.format(
      selectedValue,
      row,
      rowIndex,
    );
  }

  return normalizeCellValue(selectedValue);
};

const humanizeFieldName = (fieldName) =>
  String(fieldName)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const downloadFile = async ({
  key,
  value,
  outputFormat,
  API_ENDPOINT,
  authToken,
  fileNameProp,
  setLoadingState,
  rfqData,
  pdfLandscape = false,
  pdfPageFormat = "a4",
  pdfColumns = [],
}) => {
  try {
    setLoadingState?.(true);

    const formData = new FormData();
    const formatStr = String(
      outputFormat || "csv",
    ).toLowerCase();

    const isPdf = formatStr === "pdf";

    formData.append(key, value);
    formData.append("output_format", formatStr);

    const response = await axios.post(
      API_ENDPOINT,
      formData,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: isPdf ? "json" : "blob",
      },
    );

    const timestamp = dayjs().format("DD-MM-YYYY");
    const fileExtension = isPdf ? "pdf" : "csv";
    const filename =
      `${fileNameProp} - ${timestamp}.${fileExtension}`;

    if (!isPdf && response.data instanceof Blob) {
      const blobType = response.data.type || "";

      if (
        blobType.includes("application/json") ||
        blobType.includes("text/plain")
      ) {
        const text = await response.data.text();

        try {
          const parsed = JSON.parse(text);

          const message =
            parsed?.response ||
            parsed?.error ||
            parsed?.message ||
            "No records found to export.";

          alert(
            typeof message === "string"
              ? message
              : JSON.stringify(message),
          );
        } catch {
          alert(text || "Export failed.");
        }

        return;
      }
    }

    if (isPdf) {
      const responseRows = response?.data?.response;

      if (
        !Array.isArray(responseRows) ||
        responseRows.length === 0
      ) {
        alert(
          "No fully approved records found to generate PDF.",
        );
        return;
      }

      const pdf = createPdf({
        data: responseRows,
        fileNameProp,
        rfqData,
        pdfLandscape,
        pdfPageFormat,
        pdfColumns,
      });

      pdf.save(filename);
      return;
    }

    const blob = new Blob([response.data], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, filename);
  } catch (error) {
    console.error("Download error:", error);

    let apiMessage =
      error?.response?.data?.response ||
      error?.response?.data?.error ||
      error?.response?.data?.message;

    // Error response may be returned as Blob.
    if (error?.response?.data instanceof Blob) {
      try {
        const errorText =
          await error.response.data.text();
        const parsedError = JSON.parse(errorText);

        apiMessage =
          parsedError?.response ||
          parsedError?.error ||
          parsedError?.message;
      } catch {
        // Keep the default error message.
      }
    }

    if (apiMessage) {
      alert(
        typeof apiMessage === "string"
          ? apiMessage
          : JSON.stringify(apiMessage),
      );
    } else {
      alert("Download failed. Please try again.");
    }
  } finally {
    setLoadingState?.(false);
  }
};

const createPdf = ({
  data,
  fileNameProp,
  rfqData,
  pdfLandscape,
  pdfPageFormat,
  pdfColumns,
}) => {
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("PDF data is empty.");
  }

  const doc = rfqData
    ? new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [1920, 1080],
      })
    : new jsPDF({
        orientation: pdfLandscape
          ? "landscape"
          : "portrait",
        unit: "mm",
        format: pdfPageFormat || "a4",
      });

  /*
   * Use the provided PDF columns.
   * When no columns are provided, use all API fields.
   */
  const selectedColumns =
    Array.isArray(pdfColumns) &&
    pdfColumns.length > 0
      ? pdfColumns
      : Object.keys(data[0]).map((fieldName) => ({
          header: humanizeFieldName(fieldName),
          keys: [fieldName],
        }));

  const columns = selectedColumns.map(
    (column, index) => ({
      header:
        column.header ||
        humanizeFieldName(
          column.key || column.dataKey || "",
        ),
      dataKey: `column_${index}`,
    }),
  );

  const tableBody = data.map((row, rowIndex) => {
    const formattedRow = {};

    selectedColumns.forEach((column, columnIndex) => {
      formattedRow[`column_${columnIndex}`] =
        getColumnValue(row, column, rowIndex);
    });

    return formattedRow;
  });

  const columnStyles = selectedColumns.reduce(
    (styles, column, index) => {
      if (column.cellWidth) {
        styles[`column_${index}`] = {
          cellWidth: column.cellWidth,
        };
      }

      if (column.halign) {
        styles[`column_${index}`] = {
          ...styles[`column_${index}`],
          halign: column.halign,
        };
      }

      return styles;
    },
    {},
  );

  if (rfqData) {
    const pageWidth =
      doc.internal.pageSize.getWidth();

    const margin = 20;
    const availableWidth =
      pageWidth - margin * 2;

    const defaultColumnWidth =
      availableWidth / selectedColumns.length;

    selectedColumns.forEach((column, index) => {
      if (!columnStyles[`column_${index}`]) {
        columnStyles[`column_${index}`] = {};
      }

      if (
        !columnStyles[`column_${index}`]
          .cellWidth
      ) {
        columnStyles[`column_${index}`].cellWidth =
          defaultColumnWidth;
      }
    });

    doc.addImage(
      imgData,
      "PNG",
      12,
      3,
      22,
      8,
    );

    doc.setFontSize(12);
    doc.text(fileNameProp, 14, 16);

    autoTable(doc, {
      theme: "grid",
      columns,
      body: tableBody,
      startY: 25,

      margin: {
        left: margin,
        right: margin,
      },

      tableWidth: "auto",

      headStyles: {
        fillColor: [59, 134, 199],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },

      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },

      columnStyles,
      showHead: "everyPage",
      rowPageBreak: "avoid",
    });

    return doc;
  }

  const drawPageHeader = () => {
    doc.addImage(
      imgData,
      "PNG",
      10,
      4,
      22,
      8,
    );

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(fileNameProp, 10, 17);

    doc.setFont("helvetica", "normal");
  };

  autoTable(doc, {
    theme: "grid",
    columns,
    body: tableBody,

    // Leave space for logo and title.
    startY: 22,

    margin: {
      top: 22,
      left: 7,
      right: 7,
      bottom: 12,
    },

    tableWidth: "auto",

  headStyles: {
  fillColor: [59, 134, 199],
  textColor: [255, 255, 255],
  fontStyle: "bold",
  fontSize: 5.5,
  halign: "center",
  valign: "middle",
  overflow: "linebreak",
},

bodyStyles: {
  fontSize: 5.5,
  valign: "middle",
},

styles: {
  font: "helvetica",
  fontSize: 5.5,
  cellPadding: 1.2,
  overflow: "linebreak",
  lineWidth: 0.1,
},

    columnStyles,

    showHead: "everyPage",
    rowPageBreak: "avoid",

    /*
     * Important:
     * Do not split different columns into separate pages.
     */
    horizontalPageBreak: false,

    didDrawPage: () => {
      drawPageHeader();

      const currentPage =
        doc.internal.getCurrentPageInfo()
          .pageNumber;

      const pageWidth =
        doc.internal.pageSize.getWidth();

      const pageHeight =
        doc.internal.pageSize.getHeight();

      doc.setFontSize(7);
      doc.text(
        `Page ${currentPage}`,
        pageWidth - 22,
        pageHeight - 5,
      );
    },
  });

  return doc;
};

export { downloadFile };